import React, {
    createContext,
    useEffect,
    useState,
    useContext,
    useRef,
    useCallback,
} from "react";
import Keycloak from "keycloak-js";

export const AuthContext = createContext(null);

const IDLE_LIMIT_MS = 20 * 60 * 1000; // 20 minutes
const WARNING_SECONDS = 120; // 60 second countdown
const TOKEN_CHECK_INTERVAL_MS = 30 * 1000;
const IDLE_CHECK_INTERVAL_MS = 1000;

export const TokenHandler = ({ children }) => {
    const [keycloak, setKeycloak] = useState(null);
    const [authenticated, setAuthenticated] = useState(false);
    const [infoMessage, setInfoMessage] = useState("");
    const [givenName, setGivenName] = useState("");
    const [familyName, setFamilyName] = useState("");
    const [email, setEmail] = useState("");
    const [roles, setRoles] = useState([]);

    const [showIdleWarning, setShowIdleWarning] = useState(false);
    const [idleCountdown, setIdleCountdown] = useState(WARNING_SECONDS);

    const keycloakRef = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const lastMouseUpdateRef = useRef(0);

    const showIdleWarningRef = useRef(false);
    const countdownIntervalRef = useRef(null);
    const idleCheckIntervalRef = useRef(null);
    const tokenCheckIntervalRef = useRef(null);

    // Auto pause support
    const [autoLogoutPaused, setAutoLogoutPaused] = useState(false);
    const autoLogoutPausedRef = useRef(false);
    const keepAliveIntervalRef = useRef(null);

    useEffect(() => {
        autoLogoutPausedRef.current = autoLogoutPaused;
    }, [autoLogoutPaused]);

    useEffect(() => {
        showIdleWarningRef.current = showIdleWarning;
    }, [showIdleWarning]);

    const getTokenRemainingTime = useCallback(() => {
        const kc = keycloakRef.current;

        if (kc?.tokenParsed?.exp) {
            const now = Math.floor(Date.now() / 1000);
            return kc.tokenParsed.exp - now;
        }

        return 0;
    }, []);

    const clearCountdownTimer = () => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
    };

    const logoutUser = useCallback(() => {
        clearCountdownTimer();

        const kc = keycloakRef.current;

        if (kc) {
            kc.logout();
        } else {
            setAuthenticated(false);
            window.location.reload();
        }
    }, []);

    const startIdleWarning = useCallback(() => {
        if (showIdleWarningRef.current) return;

        showIdleWarningRef.current = true;
        setShowIdleWarning(true);
        setIdleCountdown(WARNING_SECONDS);

        clearCountdownTimer();

        countdownIntervalRef.current = setInterval(() => {
            setIdleCountdown((prev) => {
                if (prev <= 1) {
                    clearCountdownTimer();
                    logoutUser();
                    return 0;
                }

                return prev - 1;
            });
        }, 1000);
    }, [logoutUser]);

    const handleStillHere = async () => {
        const kc = keycloakRef.current;

        clearCountdownTimer();

        try {
            /*
                Force token refresh.

                updateToken(-1) tells Keycloak to refresh the token now,
                even if the access token is not close to expiring.
            */
            if (kc) {
                await kc.updateToken(-1);
            }

            lastActivityRef.current = Date.now();
            showIdleWarningRef.current = false;
            setShowIdleWarning(false);
            setIdleCountdown(WARNING_SECONDS);
        } catch (err) {
            console.warn("Failed to refresh token after idle warning. Logging out.", err);
            logoutUser();
        }
    };

    const handleLogoutClick = () => {
        logoutUser();
    };

    // Auto pause support
    useEffect(() => {
        const kc = keycloakRef.current;
        if (!kc) return;

        if (autoLogoutPaused) {
            lastActivityRef.current = Date.now();

            keepAliveIntervalRef.current = setInterval(() => {
                lastActivityRef.current = Date.now();

                kc.updateToken(300).catch((err) => {
                    console.warn("Paused token refresh failed. Will retry.", err);
                });
            }, 20000);
        } else if (keepAliveIntervalRef.current) {
            clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = null;
        }

        return () => {
            if (keepAliveIntervalRef.current) {
                clearInterval(keepAliveIntervalRef.current);
                keepAliveIntervalRef.current = null;
            }
        };
    }, [autoLogoutPaused]);

    // Track user activity
    useEffect(() => {
        const updateActivity = (e) => {
            /*
                Once the warning screen is visible, normal page activity
                should not reset the timer. Only clicking "Yes" should.
            */
            if (showIdleWarningRef.current) return;

            /*
                visibilitychange fires when the tab is hidden and when it comes back.
                Only count it as activity when the user returns to the page.
            */
            if (e.type === "visibilitychange" && document.visibilityState !== "visible") {
                return;
            }

            const now = Date.now();

            /*
                Mousemove can be noisy, but this is only useful if you add
                "mousemove" back into activityEvents later.
            */
            if (e.type === "mousemove") {
                if (now - lastMouseUpdateRef.current < 3000) return;
                lastMouseUpdateRef.current = now;
            }

            lastActivityRef.current = now;
        };

        const activityEvents = [
            "pointerdown",
            "keydown",
            "input",
            "change",
            "scroll",
            "wheel",
            "touchstart",
            "focus",
            "visibilitychange",
        ];

        activityEvents.forEach((event) => {
            window.addEventListener(event, updateActivity);
        });

        return () => {
            activityEvents.forEach((event) => {
                window.removeEventListener(event, updateActivity);
            });
        };
    }, []);

    useEffect(() => {
        const config = window.APP_CONFIG || {};
        const keycloakUrl = config.KEYCLOAK_URL;
        const keycloakRealm = config.KEYCLOAK_REALM;
        const keycloakClientId = config.KEYCLOAK_CLIENT_ID;

        if (!keycloakUrl || !keycloakRealm || !keycloakClientId) {
            console.error(
                "Missing Keycloak config. Required: KEYCLOAK_URL, KEYCLOAK_REALM, KEYCLOAK_CLIENT_ID in window.APP_CONFIG"
            );
            setInfoMessage("Configuration error: missing authentication settings.");
            return;
        }

        const initOptions = {
            url: keycloakUrl,
            realm: keycloakRealm,
            clientId: keycloakClientId,
        };

        const kc = new Keycloak(initOptions);

        kc.init({
            onLoad: "login-required",
            checkLoginIframe: false,
            pkceMethod: "S256",
        })
            .then((auth) => {
                if (!auth) {
                    console.error("User not authenticated. Redirecting to login...");
                    window.location.replace(kc.createLoginUrl());
                    return;
                }

                keycloakRef.current = kc;
                setKeycloak(kc);
                setAuthenticated(true);

                lastActivityRef.current = Date.now();

                const parsedToken = kc.idTokenParsed || {};
                setGivenName(parsedToken.given_name || "");
                setFamilyName(parsedToken.family_name || "");
                setEmail(parsedToken.email || "");

                const resourceAccess = kc.resourceAccess || {};
                const consentRoles =
                    resourceAccess["consent-user-control"]?.roles || [];

                setRoles(consentRoles);

                if (consentRoles.includes("admin")) {
                    setInfoMessage("Welcome Admin! You have full access.");
                } else if (consentRoles.includes("viewer")) {
                    setInfoMessage("Welcome Viewer! You have limited access.");
                } else {
                    setInfoMessage("Welcome! Your access level is not defined.");
                }

                /*
                    Token keep-alive logic.

                    Important:
                    - Access tokens can expire every 5 minutes.
                    - That should NOT log the user out.
                    - While the user is still inside the 20-minute idle window,
                      refresh the token quietly.
                    - Once the user reaches the 20-minute idle limit, stop silent
                      refreshing and let the idle warning control the flow.
                */
                tokenCheckIntervalRef.current = setInterval(() => {
                    if (autoLogoutPausedRef.current) {
                        kc.updateToken(300).catch((err) => {
                            console.warn("Paused token refresh failed. Will retry.", err);
                        });

                        return;
                    }

                    const idleForMs = Date.now() - lastActivityRef.current;
                    const userIsPastIdleLimit = idleForMs >= IDLE_LIMIT_MS;

                    /*
                        Once the warning is visible or the user is past idle limit,
                        do not silently refresh. The "Yes, keep me logged in" button
                        is now responsible for refreshing the session.
                    */
                    if (showIdleWarningRef.current || userIsPastIdleLimit) {
                        return;
                    }

                    /*
                        Keep token fresh while user is active or not yet idle long enough.
                        This prevents 5-minute access token expiration from logging
                        the user out before the 20-minute idle warning.
                    */
                    kc.updateToken(60)
                        .then((refreshed) => {
                            if (refreshed) {
                                console.log("Token refreshed successfully.");
                            }
                        })
                        .catch((err) => {
                            console.warn("Token refresh failed. Logging out.", err);
                            logoutUser();
                        });
                }, TOKEN_CHECK_INTERVAL_MS);

                idleCheckIntervalRef.current = setInterval(() => {
                    if (autoLogoutPausedRef.current) return;
                    if (showIdleWarningRef.current) return;

                    const idleForMs = Date.now() - lastActivityRef.current;

                    if (idleForMs >= IDLE_LIMIT_MS) {
                        startIdleWarning();
                    }
                }, IDLE_CHECK_INTERVAL_MS);
            })
            .catch((error) => {
                console.error("Authentication Failed", error);
                setInfoMessage("Authentication failed. Please try again.");
            });

        return () => {
            if (tokenCheckIntervalRef.current) {
                clearInterval(tokenCheckIntervalRef.current);
                tokenCheckIntervalRef.current = null;
            }

            if (idleCheckIntervalRef.current) {
                clearInterval(idleCheckIntervalRef.current);
                idleCheckIntervalRef.current = null;
            }

            clearCountdownTimer();

            if (keepAliveIntervalRef.current) {
                clearInterval(keepAliveIntervalRef.current);
                keepAliveIntervalRef.current = null;
            }
        };
    }, [logoutUser, startIdleWarning]);

    return (
        <AuthContext.Provider
            value={{
                keycloak,
                authenticated,
                infoMessage,
                setInfoMessage,
                givenName,
                familyName,
                email,
                roles,
                getTokenRemainingTime,

                // Auto pause
                autoLogoutPaused,
                setAutoLogoutPaused,
            }}
        >
            {children}

            {showIdleWarning && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0, 0, 0, 0.55)",
                        zIndex: 99999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "20px",
                    }}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="idle-timeout-title"
                        style={{
                            background: "#ffffff",
                            borderRadius: "14px",
                            padding: "28px",
                            maxWidth: "420px",
                            width: "100%",
                            textAlign: "center",
                            boxShadow: "0 18px 50px rgba(0,0,0,0.25)",
                        }}
                    >
                        <h2
                            id="idle-timeout-title"
                            style={{
                                marginTop: 0,
                                marginBottom: "12px",
                                fontSize: "24px",
                                color: "#111827",
                            }}
                        >
                            Are you still there?
                        </h2>

                        <p
                            style={{
                                marginBottom: "16px",
                                color: "#374151",
                                fontSize: "16px",
                                lineHeight: 1.5,
                            }}
                        >
                            For your security, you will be logged out in{" "}
                            <strong>{idleCountdown}</strong> seconds.
                        </p>

                        <div
                            style={{
                                display: "flex",
                                gap: "12px",
                                justifyContent: "center",
                                marginTop: "22px",
                                flexWrap: "wrap",
                            }}
                        >
                            <button
                                type="button"
                                onClick={handleStillHere}
                                style={{
                                    padding: "10px 18px",
                                    borderRadius: "8px",
                                    border: "none",
                                    cursor: "pointer",
                                    background: "#2563eb",
                                    color: "#ffffff",
                                    fontWeight: 600,
                                }}
                            >
                                Yes, keep me logged in
                            </button>

                            <button
                                type="button"
                                onClick={handleLogoutClick}
                                style={{
                                    padding: "10px 18px",
                                    borderRadius: "8px",
                                    border: "1px solid #d1d5db",
                                    cursor: "pointer",
                                    background: "#ffffff",
                                    color: "#111827",
                                    fontWeight: 600,
                                }}
                            >
                                No, log me out
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);